import { createContext, type ReactNode, useContext, useLayoutEffect, useMemo, useRef } from "react";
import type { MessageRichTextSubmitPayload } from "@/features/rich-text";
import type { ClaudiaSendOptions } from "../hooks/use-claudia-composer-submission";

/**
 * The composer's send action, offered to the transcript so a row can send a message without a
 * composer of its own. `canSend` is the composer's own gate, so a surface that cannot send right
 * now offers nothing a click could take.
 */
export interface ClaudiaQuickReply {
  /** Resolves `true` when the composer accepted the send, `false` when it refused or failed. */
  sendReply: (markdown: string) => Promise<boolean>;
  canSend: boolean;
  /** The thread this send lands in; a row belonging to any other thread takes no offer from it. */
  threadId: string;
}

type ComposerSend = (
  payload: MessageRichTextSubmitPayload,
  options?: ClaudiaSendOptions
) => Promise<boolean>;

const ClaudiaQuickReplyContext = createContext<ClaudiaQuickReply | null>(null);

/** The quick-reply capability of the surface above, or `null` where no composer publishes one. */
export function useClaudiaQuickReply(): ClaudiaQuickReply | null {
  return useContext(ClaudiaQuickReplyContext);
}

/**
 * Builds the value a composer publishes, or `null` while it has no thread to send into.
 *
 * The composer's `handleSend` takes a new identity whenever any of its many dependencies move,
 * which on a live thread is most renders. Reading it from a ref keeps the published value stable
 * until the offer itself changes, so consuming rows re-render when the offer changes rather than
 * when the transcript ticks.
 */
export function useClaudiaQuickReplyValue(
  send: ComposerSend,
  canSend: boolean,
  threadId: string | null
): ClaudiaQuickReply | null {
  const committedRef = useRef<{
    send: ComposerSend;
    canSend: boolean;
    threadId: string | null;
  } | null>(null);
  // Publish only committed state, before the browser can dispatch a click. A retained offer
  // must check its thread again at invocation, since its consumer may lag the composer.
  useLayoutEffect(() => {
    committedRef.current = { send, canSend, threadId };
    return () => {
      committedRef.current = null;
    };
  }, [send, canSend, threadId]);

  return useMemo(
    () =>
      threadId === null
        ? null
        : {
            canSend,
            threadId,
            sendReply: async (markdown: string) => {
              const current = committedRef.current;
              if (!current?.canSend || current.threadId !== threadId) {
                return false;
              }
              // A reply carries only the letter sentence: the composer's staged
              // attachments, voice transcripts, draft and pending intent stay where they
              // are, still available to whatever the person sends next.
              return current.send({ markdown, plainText: markdown }, { origin: "quick-reply" });
            },
          },
    [canSend, threadId]
  );
}

export function ClaudiaQuickReplyProvider({
  value,
  children,
}: {
  value: ClaudiaQuickReply | null;
  children: ReactNode;
}) {
  return (
    <ClaudiaQuickReplyContext.Provider value={value}>{children}</ClaudiaQuickReplyContext.Provider>
  );
}
